import React, { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  useTheme,
  alpha,
  Divider,
  Chip,
} from "@mui/material";
import {
  Check as CheckIcon,
  AutoAwesome as SparkleIcon,
  KeyboardArrowDown as ArrowDownIcon,
  FlashOn as FlashIcon,
  Psychology as AdvancedIcon,
} from "@mui/icons-material";
import { LANG } from "./constants";

const ModelPicker = ({ models, selectedModel, onSelect, lang }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const current = models.find((m) => m.key === selectedModel) || models[0];
  const open = Boolean(anchorEl);

  const renderModelGroup = (label, type, Icon) => {
    const list = models.filter((m) => m.model_type === type);
    if (!list.length) return null;

    return [
      <Box
        key={`header-${type}`}
        sx={{
          px: 2,
          pt: 1.5,
          pb: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <Icon sx={{ fontSize: 13, color: "primary.main" }} />
        <Typography
          variant="caption"
          sx={{
            color: "primary.main",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontSize: "0.65rem",
          }}
        >
          {label}
        </Typography>
      </Box>,

      ...list.map((m) => {
        const selected = m.key === selectedModel;

        return (
          <MenuItem
            key={m.key}
            selected={selected}
            onClick={() => {
              onSelect(m.key);
              setAnchorEl(null);
            }}
            sx={{
              gap: 1.5,
              py: 1.25,
              px: 2,
              mx: 0.75,
              borderRadius: 1.5,
              mb: 0.25,
              transition: "all 0.12s",
              minWidth: 0,
              "&.Mui-selected": {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                },
              },
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.06),
              },
            }}
          >
            <Avatar
              src={m.icon_url}
              sx={{
                width: 28,
                height: 28,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                flexShrink: 0,
                "& img": { objectFit: "contain", p: "3px" },
              }}
            />

            <Box flex={1} minWidth={0}>
              <Typography
                variant="body2"
                fontWeight={selected ? 700 : 500}
                noWrap
                sx={{
                  color: selected ? "primary.main" : "text.primary",
                  fontSize: "0.8375rem",
                  lineHeight: 1.3,
                }}
              >
                {m.name}
              </Typography>

              {m.usage_module && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  noWrap
                  sx={{ fontSize: "0.7rem", display: "block" }}
                >
                  {m.usage_module}
                </Typography>
              )}
            </Box>

            {selected ? (
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <CheckIcon
                  sx={{ fontSize: 11, color: "primary.contrastText" }}
                />
              </Box>
            ) : (
              <Box sx={{ width: 18, flexShrink: 0 }} />
            )}
          </MenuItem>
        );
      }),
    ];
  };

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: { xs: 1, sm: 1.5 },
          py: 0.75,
          borderRadius: 2.5,
          cursor: "pointer",
          border: `1.5px solid ${
            open
              ? theme.palette.primary.main
              : alpha(theme.palette.primary.main, 0.3)
          }`,
          bgcolor: open
            ? alpha(theme.palette.primary.main, 0.1)
            : alpha(theme.palette.primary.main, 0.05),
          transition: "all 0.15s ease",
          "&:hover": {
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            borderColor: theme.palette.primary.main,
          },
          minWidth: 0,
          maxWidth: { xs: 150, sm: 220 },
          width: "100%",
        }}
      >
        {current?.icon_url ? (
          <Avatar
            src={current.icon_url}
            sx={{
              width: 20,
              height: 20,
              bgcolor: "transparent",
              flexShrink: 0,
              "& img": { objectFit: "contain" },
            }}
          />
        ) : (
          <SparkleIcon
            sx={{ fontSize: 16, color: "primary.main", flexShrink: 0 }}
          />
        )}

        <Typography
          noWrap
          sx={{
            fontSize: "0.8rem",
            fontWeight: 700,
            color: "primary.main",
            lineHeight: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          {current?.short_name ||
            current?.name ||
            lang?.selectModel ||
            LANG.selectModel}
        </Typography>

        {current?.model_type && (
          <Chip
            label={current.model_type === "advance" ? "Pro" : "Basic"}
            size="small"
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              height: 16,
              fontSize: "0.58rem",
              fontWeight: 700,
              bgcolor:
                current.model_type === "advance"
                  ? alpha(theme.palette.warning.main, 0.15)
                  : alpha(theme.palette.success.main, 0.12),
              color:
                current.model_type === "advance"
                  ? "warning.main"
                  : "success.main",
              border: `1px solid ${
                current.model_type === "advance"
                  ? alpha(theme.palette.warning.main, 0.3)
                  : alpha(theme.palette.success.main, 0.25)
              }`,
              "& .MuiChip-label": { px: 0.75 },
              flexShrink: 0,
            }}
          />
        )}

        <ArrowDownIcon
          sx={{
            fontSize: 16,
            color: "primary.main",
            flexShrink: 0,
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          elevation: 0,
          sx: {
            maxHeight: 420,
            width: { xs: "calc(100vw - 24px)", sm: 300 },
            maxWidth: 300,
            overflow: "auto",
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: isDark
              ? `0 8px 32px ${alpha(theme.palette.common.black, 0.5)}`
              : `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
            borderRadius: 2.5,
            mt: 0.5,
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha(theme.palette.text.primary, 0.12),
              borderRadius: 2,
            },
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography
            variant="body2"
            fontWeight={700}
            color="text.primary"
            sx={{ fontSize: "0.875rem" }}
          >
            {lang?.selectModel || LANG.selectModel}
          </Typography>

          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontSize: "0.7rem" }}
          >
            {models.length} models available
          </Typography>
        </Box>

        <Divider sx={{ mb: 0.5 }} />

        {renderModelGroup("Basic", "basic", FlashIcon)}
        {renderModelGroup("Advanced", "advance", AdvancedIcon)}

        <Box sx={{ pb: 0.75 }} />
      </Menu>
    </>
  );
};

export default ModelPicker;
